import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Calendar, ArrowRight, Heart, Zap, Shield, Star, Clock, Plus, Users, Phone, ChevronRight, LayoutDashboard } from "lucide-react";
import { Link } from "wouter";
import MemberLayout from "@/layouts/MemberLayout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetMemberAppointments,
  useListTopRatedProviders,
  useListCareTeam, getListCareTeamQueryKey,
} from "@/lib/api-client-react";

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const quickActions = [
  { label: "Find a Provider", icon: Heart, href: "/member/search", color: "from-blue-500 to-cyan-400" },
  { label: "Good Faith Estimates", icon: Zap, href: "/member/cost-estimates", color: "from-violet-500 to-purple-400" },
  { label: "My Card", icon: Shield, href: "/member/membership-card", color: "from-emerald-500 to-teal-400" },
  { label: "My Team", icon: Star, href: "/member/care-team", color: "from-amber-500 to-orange-400" },
];

export default function MemberDashboard() {
  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch(e) {}
    }
  }, []);

  const { data: appointmentsData, isLoading } = useGetMemberAppointments();
  const { data: topProviders } = useListTopRatedProviders();
  const { data: careTeamData, isLoading: careTeamLoading } = useListCareTeam({ query: { queryKey: getListCareTeamQueryKey() } });

  const careTeam = careTeamData?.items || [];
  const appointments = appointmentsData?.items || [];
  
  // Only show upcoming approved appointments
  const activeAppointments = appointments.filter((a: any) => a.status === 'APPROVED');
  const nextAppt = activeAppointments.length > 0 ? activeAppointments[0] : null;

  return (
    <MemberLayout>
      <div className="w-full space-y-6">

        {/* Greeting Header */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="flex items-center gap-3 sm:gap-4 bg-card p-4 sm:p-5 rounded-2xl border border-border shadow-xs">
          <div className="p-2.5 sm:p-3 bg-primary/10 rounded-xl shrink-0">
            <LayoutDashboard className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-xs sm:text-sm font-medium text-primary mb-0.5">{getGreeting()} 👋</p>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground truncate">Welcome back, {user?.firstName || "Jane"}</h1>
            <p className="text-muted-foreground mt-0.5 text-xs sm:text-sm">Here's your health at a glance.</p>
          </div>
        </motion.div>

        {/* ── Top section: Appointment hero + Quick Actions (side by side on desktop) ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 items-stretch">

          {/* Next Appointment Hero */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="lg:col-span-7 xl:col-span-8 flex flex-col">
            {isLoading ? (
              <div className="bg-card rounded-2xl sm:rounded-3xl p-6 shadow-xs border border-border h-full flex flex-col justify-center">
                <Skeleton className="h-4 w-32 mb-3" />
                <Skeleton className="h-6 w-48 mb-2" />
                <Skeleton className="h-4 w-36" />
              </div>
            ) : nextAppt ? (
              <div
                className="relative rounded-2xl sm:rounded-3xl p-5 sm:p-7 text-white overflow-hidden shadow-lg shadow-blue-600/10 h-full border border-white/10 flex flex-col justify-between"
                style={{ background: "linear-gradient(135deg, #1a3a6b 0%, #2563EB 40%, #06B6D4 100%)" }}
                data-testid="next-appointment"
              >
                {/* Decorative background elements */}
                <div className="absolute -right-20 -top-20 w-72 h-72 bg-white/10 blur-3xl rounded-full pointer-events-none" />
                <div className="absolute -left-10 -bottom-10 w-48 h-48 bg-cyan-500/20 blur-2xl rounded-full pointer-events-none" />
                <div className="absolute right-8 top-8 w-24 h-24 border border-white/20 rounded-full opacity-50 pointer-events-none" />
                
                <div className="relative h-full flex flex-col justify-between z-10 space-y-6">
                  <div>
                    <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                      <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                      <p className="text-white/80 text-xs font-bold uppercase tracking-[0.2em]">Next Appointment</p>
                    </div>
                    
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight mb-1.5 sm:mb-2 text-white break-words">
                          {nextAppt.provider?.user?.displayName || `${nextAppt.provider?.user?.firstName} ${nextAppt.provider?.user?.lastName}`}
                        </h2>
                        <p className="text-blue-100 text-xs sm:text-sm font-medium">{nextAppt.bundle?.name || 'General Visit'}</p>
                        
                        <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 mt-4 sm:mt-6">
                          <div className="flex items-center gap-2 backdrop-blur-md bg-white/10 border border-white/20 rounded-xl px-3 sm:px-4 py-2">
                            <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-200 shrink-0" />
                            <span className="text-xs sm:text-sm font-semibold tracking-wide whitespace-nowrap">{new Date(nextAppt.createdAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-2 backdrop-blur-md bg-white/10 border border-white/20 rounded-xl px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold tracking-wide whitespace-nowrap">
                            {new Date(nextAppt.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </div>
                        </div>
                      </div>
                      <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-2xl backdrop-blur-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0 text-2xl sm:text-3xl lg:text-4xl font-black shadow-inner">
                        {(nextAppt.provider?.user?.firstName?.[0] || nextAppt.provider?.user?.displayName?.[0] || 'D')}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 pt-4 sm:pt-6 border-t border-white/10 items-center">
                    <Link href={`/member/appointments?search=${encodeURIComponent(nextAppt.provider?.user?.displayName || nextAppt.provider?.user?.firstName || '')}`}>
                      <Button className="bg-white text-indigo-700 hover:bg-blue-50 rounded-xl font-bold border-0 h-10 sm:h-11 px-6 sm:px-8 text-xs sm:text-sm shadow-[0_0_15px_rgba(255,255,255,0.3)] transition-all hover:shadow-[0_0_25px_rgba(255,255,255,0.5)] hover:-translate-y-0.5">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-card rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-dashed border-border text-center shadow-xs h-full flex flex-col items-center justify-center space-y-3" data-testid="no-appointments">
                <Calendar className="w-10 h-10 text-muted-foreground/40 mx-auto" />
                <div className="space-y-1">
                  <p className="font-bold text-foreground text-sm sm:text-base">No upcoming appointments</p>
                  <p className="text-muted-foreground text-xs sm:text-sm">Find a provider and book your next visit</p>
                </div>
                <Link href="/member/search">
                  <Button className="rounded-xl bg-primary text-primary-foreground border-0 gap-2 h-9 px-5 text-xs sm:text-sm font-semibold">
                    <Plus className="w-4 h-4" /> Book Now
                  </Button>
                </Link>
              </div>
            )}
          </motion.div>

          {/* Quick Actions */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-5 xl:col-span-4 flex flex-col">
            <div className="bg-card rounded-2xl sm:rounded-3xl p-4 sm:p-5 border border-border shadow-xs flex-1 flex flex-col">
              <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 sm:mb-4">Quick Actions</h2>
              <div className="grid grid-cols-2 gap-3 flex-1">
                {quickActions.map((action) => (
                  <Link key={action.href} href={action.href} className="block h-full">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                      className="bg-muted/40 hover:bg-muted/70 rounded-xl sm:rounded-2xl p-3.5 sm:p-4 border border-border/60 hover:border-primary/30 hover:shadow-xs transition-all cursor-pointer group flex flex-col justify-between h-full min-h-[105px]"
                      data-testid={`quick-action-${action.label.replace(/ /g, "-").toLowerCase()}`}
                    >
                      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center shadow-xs mb-2 shrink-0`}>
                        <action.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <p className="text-xs sm:text-sm font-semibold text-foreground group-hover:text-primary transition-colors leading-tight">{action.label}</p>
                    </motion.div>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Care Team + Upcoming Visits */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 items-stretch">

          {/* Care Team */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="lg:col-span-5 flex flex-col">
            <div className="bg-card rounded-2xl sm:rounded-3xl border border-border shadow-xs overflow-hidden flex-1 flex flex-col">
              <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 border-b border-border bg-card">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-primary/10 rounded-lg">
                    <Users className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="font-bold text-foreground text-sm">Care Team</h2>
                </div>
                <Link href="/member/care-team">
                  <span className="text-primary text-xs font-semibold cursor-pointer hover:underline flex items-center gap-0.5">
                    View all <ChevronRight className="w-3 h-3" />
                  </span>
                </Link>
              </div>

              <div className="divide-y divide-border/60 flex-1">
                {careTeamLoading ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 sm:px-5 py-3.5">
                    <Skeleton className="w-9 h-9 rounded-xl shrink-0" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-28 mb-1" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                )) : careTeam.length === 0 ? (
                  <div className="py-10 text-center px-5 flex flex-col items-center justify-center h-full">
                    <Users className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground mb-3 font-medium">No providers added yet</p>
                    <Link href="/member/search">
                      <Button size="sm" className="rounded-xl bg-primary text-primary-foreground border-0 h-8 px-4 text-xs gap-1 font-semibold">
                        <Plus className="w-3.5 h-3.5" /> Add a Provider
                      </Button>
                    </Link>
                  </div>
                ) : careTeam.slice(0, 4).map((member: any) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 px-4 sm:px-5 py-3.5 hover:bg-muted/40 transition-colors"
                    data-testid={`dash-care-member-${member.id}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 text-primary font-bold text-sm uppercase">
                      {(member.name || member.providerName)?.charAt(0) || 'D'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-foreground truncate leading-tight">{member.name || member.providerName}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{member.specialty || member.role}</p>
                    </div>
                    {member.phone && (
                      <a href={`tel:${member.phone}`} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors shrink-0">
                        <Phone className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Upcoming Visits */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-7 flex flex-col">
            <div className="bg-card rounded-2xl sm:rounded-3xl border border-border shadow-xs overflow-hidden flex-1 flex flex-col">
              <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 border-b border-border bg-card">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-primary/10 rounded-lg">
                    <Calendar className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="font-bold text-foreground text-sm">Upcoming Visits</h2>
                </div>
                <Link href="/member/search">
                  <Button variant="ghost" size="sm" className="text-primary text-xs gap-1 h-7 px-2.5 rounded-lg hover:bg-primary/10 font-semibold">
                    Book <ArrowRight className="w-3 h-3" />
                  </Button>
                </Link>
              </div>

              <div className="divide-y divide-border/60 flex-1">
                {isLoading ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-4 px-4 sm:px-5 py-4">
                    <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
                    <div className="flex-1"><Skeleton className="h-4 w-36 mb-1.5" /><Skeleton className="h-3 w-24" /></div>
                    <Skeleton className="h-4 w-20 rounded-full" />
                  </div>
                )) : activeAppointments.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground flex flex-col items-center justify-center h-full space-y-2">
                    <Calendar className="w-8 h-8 mx-auto opacity-30" />
                    <p className="text-sm font-medium">No upcoming visits</p>
                    <Link href="/member/search">
                      <Button variant="link" className="text-primary text-xs h-7 font-semibold">Find a Provider</Button>
                    </Link>
                  </div>
                ) : activeAppointments.map((appt: any) => (
                  <div key={appt.id} className="flex items-center justify-between gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 sm:py-4 hover:bg-muted/40 transition-colors" data-testid={`appt-${appt.id}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 text-primary font-bold text-sm uppercase">
                        {(appt.provider?.user?.firstName?.[0] || appt.provider?.user?.displayName?.[0] || 'D')}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-sm text-foreground truncate">
                          {appt.provider?.user?.displayName || `${appt.provider?.user?.firstName} ${appt.provider?.user?.lastName}`}
                        </p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{appt.bundle?.name || 'General Visit'}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-bold text-foreground">{new Date(appt.createdAt).toLocaleDateString()}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{new Date(appt.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

      </div>
    </MemberLayout>
  );
}
